import React, { ChangeEvent, Fragment, useState } from 'react';
import { Alert, Button, Container, Form, InputGroup } from 'react-bootstrap';
import { CompanyResource } from '../../common/entities';
import EcosystemNameSelect from './EcosystemNameSelect';
import { DealResource, defaultDealResource } from '../../common/entities';
import { Option } from 'react-bootstrap-typeahead/types/types';
import DealsDataService from '../../common/services/DealsDataService';
import RequiredIcon from '../../common/components/RequiredIcon';
import QuarterSelect from './QuarterSelect';
import RoundTypeSelect from './RoundTypeSelect';
import { format, parseISO } from 'date-fns';

interface Props {
  company: CompanyResource;
  onSave: (deal: DealResource) => void;
}

const getQuarter = (date = new Date()) => {
  return Math.floor(date.getMonth() / 3 + 1);
};

/**
 * @description High level component for dealing with Briefed.in marketplace.
 * @param {Props} props
 * @return {JSX.Element}
 */
const DealForm = (props: Props): JSX.Element => {
  const company = props.company.attributes;
  const initialDeal = defaultDealResource();
  initialDeal.attributes.companyId = company.id;
  initialDeal.attributes.companyName = company.companyName;
  initialDeal.attributes.primaryTag = company.primaryTag;
  initialDeal.attributes.ecosystemName = company.ecosystemName;
  initialDeal.attributes.quarter = `Q${getQuarter()}`;
  // initialDeal.attributes.date = new Date();
  initialDeal.attributes.year = String(new Date().getFullYear());
  const [data, setData] = useState<DealResource>(initialDeal);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [validated, setValidated] = useState<boolean>();
  const [brief1, setBrief1] = useState<string>();
  const [brief2, setBrief2] = useState<string>();
  const [brief3, setBrief3] = useState<string>();
  const [brief4, setBrief4] = useState<string>();
  const [brief5, setBrief5] = useState<string>();

  const onChangeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const newData = { ...data };
    newData.attributes[event.target.name] = event.target.value;
    setData(newData);
  };

  const onTypeaheadSelectSingle = (name: string, selected: Option[]) => {
    const newData = { ...data };
    newData.attributes[name] = selected[0] as string;
    setData(newData);
  };

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const newData = { ...data };
    console.log('DATA', event.target.value);
    newData.attributes[event.target.name] = event.target.value;
    if (event.target.name === 'date') {
      const date = parseISO(event.target.value);
      newData.attributes.quarter = `Q${getQuarter(date)}`;
      newData.attributes.year = String(date.getFullYear());
    }
    console.log(newData);
    setData(newData);
  };

  const onFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    const valid = form.checkValidity();

    const brief = [];
    if (brief1) {
      brief.push('- ' + brief1);
    }
    if (brief2) {
      brief.push('- ' + brief2);
    }
    if (brief3) {
      brief.push('- ' + brief3);
    }
    if (brief4) {
      brief.push('- ' + brief4);
    }
    if (brief5) {
      brief.push('- ' + brief5);
    }

    data.attributes.brief = brief.join('\n');
    console.log(data);
    // props.onSave(data);
    // mark the form as having its validity checked
    setValidated(true);
    //
    if (!valid) {
      console.log('not valid');
      return;
    }
    //
    // setBusy(true);
    // setErrorMessage('');
    setErrorMessage(undefined);
    try {
      const newDeal = await DealsDataService.create(data);
      // setBusy(false);
      props.onSave(newDeal);
    } catch (err) {
      setErrorMessage('There was an error saving this deal.');
      console.error('.', err, data);
      // setBusy(false);
    }
  };

  return (
    <Fragment>
      <Container>
        <Form noValidate validated={validated} onSubmit={onFormSubmit}>
          <Form.Group className="mb-3" controlId="companyNameInput">
            <Form.Label>
              Company Name <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ACME Inc."
              value={data.attributes.companyName}
              name={'companyName'}
              required={true}
              readOnly={true}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="primaryTagInput">
            <Form.Label>
              Primary Tag <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="text"
              value={data.attributes.primaryTag}
              name={'primaryTag'}
              required={true}
              readOnly={true}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="ecosystemNameInput">
            <Form.Label>
              Ecosystem Name <RequiredIcon />
            </Form.Label>
            <EcosystemNameSelect
              value={data.attributes.ecosystemName}
              name={'ecosystemName'}
              required={true}
              disabled={true}
              onChange={onChangeSelect}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="articleUrlInput">
            <Form.Label>
              Article URL <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com/acme-seed-100"
              value={data.attributes.articleUrl}
              name={'articleUrl'}
              required={true}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="leadInvestorsInput">
            <Form.Label>Lead Investors</Form.Label>
            <Form.Control
              type="text"
              placeholder="Invest Co, Give Money"
              value={data.attributes.leadInvestors}
              name={'leadInvestors'}
              onChange={onChangeInput}
            />
            <Form.Text>Investor names only - comma separated.</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="investorsInput">
            <Form.Label>Investors</Form.Label>
            <Form.Control
              type="text"
              placeholder="Invest Co, Give Money"
              value={data.attributes.investors}
              name={'investors'}
              onChange={onChangeInput}
            />
            <Form.Text>
              Investors should NOT include lead investors. Investor names only -
              comma separated.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="roundTypeInput">
            <Form.Label>Round Type</Form.Label>
            <RoundTypeSelect
              required={true}
              onChange={onChangeSelect}
              value={data.attributes.roundType}
              name={'roundType'}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="dateInput">
            <Form.Label>
              Date <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="date"
              placeholder=""
              name={'date'}
              required={true}
              value={data.attributes.date}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="investorsInput">
            <Form.Label>
              Amount (CAD) <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="1000000"
              value={data.attributes.amount}
              name={'amount'}
              required={true}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="yearInput">
            <Form.Label>
              Year <RequiredIcon />
            </Form.Label>
            <Form.Control
              readOnly={true}
              type="text"
              placeholder="2022"
              value={data.attributes.year}
              name={'year'}
              required={true}
              onChange={onChangeInput}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="yearInput">
            <Form.Label>
              Quarter <RequiredIcon />
            </Form.Label>
            <QuarterSelect
              readOnly={true}
              required={true}
              onChange={onChangeSelect}
              name={'quarter'}
              value={data.attributes.quarter}
            />
            <Form.Control.Feedback type="invalid">
              This field is required.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="yearInput">
            <Form.Label>
              Brief <RequiredIcon />
            </Form.Label>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon1">1.</InputGroup.Text>
              <Form.Control
                required={true}
                placeholder=""
                name={'brief1'}
                value={brief1}
                onChange={(event) => setBrief1(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon2">2.</InputGroup.Text>
              <Form.Control
                required={true}
                placeholder=""
                name={'brief2'}
                value={brief2}
                onChange={(event) => setBrief2(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon3">3.</InputGroup.Text>
              <Form.Control
                required={false}
                placeholder=""
                name={'brief3'}
                value={brief3}
                onChange={(event) => setBrief3(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon4">4.</InputGroup.Text>
              <Form.Control
                required={false}
                placeholder=""
                name={'brief4'}
                value={brief4}
                onChange={(event) => setBrief4(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon5">5.</InputGroup.Text>
              <Form.Control
                required={false}
                placeholder=""
                name={'brief5'}
                value={brief5}
                onChange={(event) => setBrief5(event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3" controlId="articleTitleInput">
            <Form.Label>Article Title</Form.Label>
            <Form.Control
              type="text"
              placeholder=""
              value={data.attributes.articleTitle}
              name={'articleTitle'}
              onChange={onChangeInput}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="articleDescriptionInput">
            <Form.Label>Article Description</Form.Label>
            <Form.Control
              type="textarea"
              placeholder=""
              value={data.attributes.articleDescription}
              name={'articleDescription'}
              onChange={onChangeInput}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="articleSiteNameInput">
            <Form.Label>
              Article Site Name <RequiredIcon />
            </Form.Label>
            <Form.Control
              type="text"
              placeholder=""
              required={true}
              value={data.attributes.articleSiteName}
              name={'articleSiteName'}
              onChange={onChangeInput}
            />
          </Form.Group>
          <Button variant="primary" type="submit">
            Save Deal
          </Button>
          {errorMessage ? (
            <Alert variant={'danger'}>
              <Alert.Heading>Error</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          ) : null}
        </Form>
      </Container>
    </Fragment>
  );
};

export default DealForm;
